/**
 * Global Search Command Palette
 *
 * A command-palette style search UI that opens with ⌘K / Ctrl+K.
 * Uses the SearchProvider abstraction for future-proof backend swapping.
 *
 * Features:
 * - Search scopes (v, k, si, pi, wo, p)
 * - Recent searches & recently opened
 * - Highlighted matched text
 * - Clear relationship hints in subtitles
 * - Loading / empty / error states
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/lib/logger';
import { useTranslation } from 'react-i18next';
import {
  Car,
  Users,
  FileText,
  Receipt,
  Loader2,
  Wrench,
  Package,
  Clock,
  Search,
  AlertCircle,
} from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { useTeam } from '@/contexts/TeamContext';
import { useGlobalSearch } from './useGlobalSearch';
import { firestoreSearchProvider } from './FirestoreSearchProvider';
import { openSearchResult } from './openSearchResult';
import { parseSearchQuery, getScopeLabel, getAvailableScopes } from './parseSearchQuery';
import { getRecentItems, getRecentQueries, addRecentItem, addRecentQuery } from './recents';
import { HighlightedText } from './HighlightedText';
import type { GlobalSearchResult, EntityType } from './SearchProvider';
import { ENTITY_TYPE_LABELS } from './SearchProvider';
import type { RecentItem, RecentQuery } from './recents';

const ENTITY_ICONS: Record<EntityType, React.ElementType> = {
  vehicle: Car,
  customer: Users,
  salesInvoice: FileText,
  purchaseInvoice: Receipt,
  workOrder: Wrench,
  part: Package,
};

const ENTITY_BADGE_VARIANTS: Record<EntityType, 'default' | 'secondary' | 'outline'> = {
  vehicle: 'default',
  customer: 'secondary',
  salesInvoice: 'outline',
  purchaseInvoice: 'outline',
  workOrder: 'default',
  part: 'secondary',
};

const MIN_QUERY_LENGTH = 2;

export function GlobalSearch() {
  const { t } = useTranslation('common');
  const { isOpen, close } = useGlobalSearch();
  const navigate = useNavigate();
  const { activeTeamId } = useTeam();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentItems, setRecentItems] = useState<RecentItem[]>([]);
  const [recentQueries, setRecentQueries] = useState<RecentQuery[]>([]);

  const teamId = activeTeamId;

  // Parse query for scope
  const parsedQuery = useMemo(() => parseSearchQuery(query), [query]);
  const scopeLabel = useMemo(() => getScopeLabel(parsedQuery.scope), [parsedQuery.scope]);
  const actualQuery = parsedQuery.query;

  // Load recents when dialog opens
  useEffect(() => {
    if (isOpen && teamId) {
      setRecentItems(getRecentItems(teamId));
      setRecentQueries(getRecentQueries(teamId));
    }
  }, [isOpen, teamId]);

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;

    // Clear error on new search
    setError(null);

    if (actualQuery.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const scope = parsedQuery.scope === 'all' ? undefined : parsedQuery.scope;
          const data = await firestoreSearchProvider.search(actualQuery, teamId, { scope });
          setResults(data);
          // Add to recent queries on successful search
          addRecentQuery(teamId, query);
          setRecentQueries(getRecentQueries(teamId));
        } catch (err) {
          logger.error('[GlobalSearch] Search failed:', err);
          setError(t('globalSearch.error', 'Zoeken mislukt'));
          setResults([]);
        } finally {
          setLoading(false);
        }
      })();
    }, 200);

    return () => clearTimeout(timer);
  }, [actualQuery, teamId, isOpen, parsedQuery.scope, query, t]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
      setLoading(false);
      setError(null);
    }
  }, [isOpen]);

  // Group results by entity type
  const groupedResults = useMemo(() => {
    const groups: Record<EntityType, GlobalSearchResult[]> = {
      vehicle: [],
      customer: [],
      salesInvoice: [],
      purchaseInvoice: [],
      workOrder: [],
      part: [],
    };

    results.forEach((result) => {
      groups[result.entityType].push(result);
    });

    return groups;
  }, [results]);

  const handleSelect = useCallback(
    (result: GlobalSearchResult) => {
      // Add to recently opened
      addRecentItem(teamId, {
        entityType: result.entityType,
        id: result.id,
        title: result.title,
        subtitle: result.subtitle,
        openTarget: result.openTarget,
      });
      close();
      openSearchResult(result, navigate);
    },
    [teamId, close, navigate]
  );

  const handleRecentItemSelect = useCallback(
    (item: RecentItem) => {
      close();
      navigate(
        `${item.openTarget.route}?dialog=${encodeURIComponent(item.openTarget.dialog)}&mode=view&id=${encodeURIComponent(item.openTarget.idParam)}`
      );
    },
    [close, navigate]
  );

  const handleRecentQuerySelect = useCallback((recentQuery: RecentQuery) => {
    setQuery(recentQuery.query);
  }, []);

  const hasResults =
    groupedResults.vehicle.length > 0 ||
    groupedResults.customer.length > 0 ||
    groupedResults.salesInvoice.length > 0 ||
    groupedResults.purchaseInvoice.length > 0 ||
    groupedResults.workOrder.length > 0 ||
    groupedResults.part.length > 0;

  const showRecents = !query && (recentItems.length > 0 || recentQueries.length > 0);

  const renderResultGroup = (entityType: EntityType, items: GlobalSearchResult[]) => {
    if (items.length === 0) return null;

    const Icon = ENTITY_ICONS[entityType];
    const label = ENTITY_TYPE_LABELS[entityType];
    const badgeVariant = ENTITY_BADGE_VARIANTS[entityType];

    return (
      <>
        <CommandSeparator />
        <CommandGroup heading={label}>
          {items.map((result) => (
            <CommandItem
              key={`${result.entityType}-${result.id}`}
              value={`${result.title} ${result.subtitle || ''}`}
              onSelect={() => handleSelect(result)}
              className="cursor-pointer"
            >
              <Icon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="flex flex-1 flex-col min-w-0">
                <HighlightedText
                  text={result.title}
                  query={actualQuery}
                  className="truncate font-medium"
                />
                {result.subtitle && (
                  <HighlightedText
                    text={result.subtitle}
                    query={actualQuery}
                    className="truncate text-xs text-muted-foreground"
                  />
                )}
              </div>
              <Badge variant={badgeVariant} className="ml-2 shrink-0 text-xs">
                {label}
              </Badge>
            </CommandItem>
          ))}
        </CommandGroup>
      </>
    );
  };

  const renderRecents = () => {
    if (!showRecents) return null;

    return (
      <>
        {recentItems.length > 0 && (
          <CommandGroup heading={t('globalSearch.recentlyOpened', 'Recent geopend')}>
            {recentItems.map((item) => {
              const Icon = ENTITY_ICONS[item.entityType];
              const label = ENTITY_TYPE_LABELS[item.entityType];
              const badgeVariant = ENTITY_BADGE_VARIANTS[item.entityType];

              return (
                <CommandItem
                  key={`recent-${item.entityType}-${item.id}`}
                  value={`recent ${item.title} ${item.subtitle || ''}`}
                  onSelect={() => handleRecentItemSelect(item)}
                  className="cursor-pointer"
                >
                  <Clock className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                  <Icon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="flex flex-1 flex-col min-w-0">
                    <span className="truncate font-medium">{item.title}</span>
                    {item.subtitle && (
                      <span className="truncate text-xs text-muted-foreground">
                        {item.subtitle}
                      </span>
                    )}
                  </div>
                  <Badge variant={badgeVariant} className="ml-2 shrink-0 text-xs">
                    {label}
                  </Badge>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {recentQueries.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={t('globalSearch.recentSearches', 'Recente zoekopdrachten')}>
              {recentQueries.map((rq) => (
                <CommandItem
                  key={`query-${rq.ts}`}
                  value={`query ${rq.query}`}
                  onSelect={() => handleRecentQuerySelect(rq)}
                  className="cursor-pointer"
                >
                  <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{rq.query}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </>
    );
  };

  const renderEmptyState = () => {
    // Error state
    if (error) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 py-6">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <span className="text-sm font-medium text-destructive">{error}</span>
          <span
            role="button"
            tabIndex={0}
            onClick={() => {
              setError(null);
              setQuery(query + ' '); // Trigger re-search
              setTimeout(() => setQuery(query), 0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setError(null);
                setQuery(query + ' ');
                setTimeout(() => setQuery(query), 0);
              }
            }}
            className="text-xs text-muted-foreground hover:text-foreground underline cursor-pointer"
          >
            {t('globalSearch.tryAgain', 'Probeer opnieuw')}
          </span>
        </div>
      );
    }

    // Loading state
    if (loading) {
      return (
        <div className="flex items-center justify-center gap-2 py-6">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm text-muted-foreground">
            {t('globalSearch.searching', 'Zoeken...')}
          </span>
        </div>
      );
    }

    // No query yet - show hint or recents
    if (actualQuery.length < MIN_QUERY_LENGTH) {
      if (showRecents) {
        return null; // Recents will be shown instead
      }
      return (
        <div className="py-6 text-center">
          <p className="text-sm text-muted-foreground mb-2">
            {t('globalSearch.hint', 'Begin met typen om voertuigen, klanten en facturen te zoeken')}
          </p>
          <div className="flex flex-wrap justify-center gap-1 mt-3">
            {getAvailableScopes().map(({ prefix, label }) => (
              <Badge key={prefix} variant="outline" className="text-xs">
                <code className="mr-1 font-mono">{prefix}</code>
                {label}
              </Badge>
            ))}
          </div>
        </div>
      );
    }

    // No results
    return (
      <div className="py-6 text-center">
        <p className="text-sm text-muted-foreground mb-2">
          {t('globalSearch.noResultsFor', "Geen resultaten voor '{{query}}'", {
            query: actualQuery,
          })}
        </p>
        <p className="text-xs text-muted-foreground">
          {t('globalSearch.searchHint', 'Probeer een kenteken, VIN, factuurnummer of klantnaam')}
        </p>
        <div className="flex flex-wrap justify-center gap-1 mt-3">
          {getAvailableScopes().map(({ prefix, label }) => (
            <Badge key={prefix} variant="outline" className="text-xs">
              <code className="mr-1 font-mono">{prefix}</code>
              {label}
            </Badge>
          ))}
        </div>
      </div>
    );
  };

  return (
    <CommandDialog open={isOpen} onOpenChange={close}>
      <div className="relative">
        <CommandInput
          placeholder={t('globalSearch.placeholder', 'Zoek voertuigen, klanten, facturen...')}
          value={query}
          onValueChange={setQuery}
        />
        {scopeLabel && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Badge variant="secondary" className="text-xs">
              {scopeLabel}
            </Badge>
          </div>
        )}
      </div>
      <CommandList>
        <CommandEmpty>{renderEmptyState()}</CommandEmpty>

        {showRecents && renderRecents()}

        {hasResults && (
          <>
            {renderResultGroup('vehicle', groupedResults.vehicle)}
            {renderResultGroup('customer', groupedResults.customer)}
            {renderResultGroup('workOrder', groupedResults.workOrder)}
            {renderResultGroup('part', groupedResults.part)}
            {renderResultGroup('salesInvoice', groupedResults.salesInvoice)}
            {renderResultGroup('purchaseInvoice', groupedResults.purchaseInvoice)}
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
